<template>
  <!-- DUPLICATED from packages/frontend for the studio (intentional — see
       docs/superpowers/specs/2026-06-01-battleplan-authoring-tool-design.md §5).
       Keep rendering in sync with the frontend original when it changes. -->
  <div class="battleplan-map">
    <div class="map-container">
      <svg :viewBox="`0 0 ${W} ${H}`" class="battleplan-svg" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" class="arrowhead" />
          </marker>
        </defs>

        <!-- Background -->
        <rect x="0" y="0" :width="W" :height="H" class="map-bg" />

        <!-- Zones -->
        <rect
          v-for="(zone, i) in map.zones"
          :key="'zone-' + i"
          :x="s(zone.x1)"
          :y="s(zone.y1)"
          :width="s(zone.x2 - zone.x1)"
          :height="s(zone.y2 - zone.y1)"
          :class="'zone zone-' + zone.type"
        />

        <!-- Minor grid lines (every 15" width, every 11" height) -->
        <line
          v-for="gx in minorGridX"
          :key="'gx-' + gx"
          :x1="s(gx)"
          y1="0"
          :x2="s(gx)"
          :y2="H"
          class="grid-line-minor"
        />
        <line
          v-for="gy in minorGridY"
          :key="'gy-' + gy"
          x1="0"
          :y1="s(gy)"
          :x2="W"
          :y2="s(gy)"
          class="grid-line-minor"
        />

        <!-- Major grid lines (center: 30" width, 22" height) -->
        <line :x1="s(30)" y1="0" :x2="s(30)" :y2="H" class="grid-line-major" />
        <line x1="0" :y1="s(22)" :x2="W" :y2="s(22)" class="grid-line-major" />

        <!-- Border -->
        <rect x="0" y="0" :width="W" :height="H" class="map-border" />

        <!-- Terrain pieces -->
        <g v-for="(t, i) in map.terrain" :key="'terrain-' + i">
          <circle
            :cx="s(t.x)"
            :cy="s(t.y)"
            :r="terrainRadius(t.size)"
            :class="'terrain terrain-' + t.type"
          />
          <path
            :d="terrainIconPath(t.type)"
            :transform="terrainIconTransform(t.type, s(t.x), s(t.y), 14)"
            class="terrain-icon-svg"
          />
        </g>

        <!-- Objectives -->
        <g v-for="(obj, i) in map.objectives" :key="'obj-' + i">
          <circle
            :cx="s(obj.x)"
            :cy="s(obj.y)"
            :r="s(OBJ_RADIUS)"
            :class="'objective objective-' + obj.type"
          />
          <path
            v-if="objectiveIconDef(obj.type)"
            :d="objectiveIconPath(obj.type)"
            :transform="objectiveIconTransform(obj.type, s(obj.x), s(obj.y), 22)"
            class="objective-icon-svg"
          />
          <text v-else :x="s(obj.x)" :y="s(obj.y)" class="objective-label">
            {{ objectiveLabel(obj.type) }}
          </text>
        </g>

        <!-- Zone labels -->
        <text
          v-for="(zone, i) in map.zones.filter((z) => !z.hideLabel)"
          :key="'zlabel-' + i"
          :x="s((zone.x1 + zone.x2) / 2 + (zone.labelDx ?? 0))"
          :y="s((zone.y1 + zone.y2) / 2 + (zone.labelDy ?? 0))"
          class="zone-label"
        >
          <tspan
            v-for="(line, li) in zoneLabelLines(zone)"
            :key="li"
            :x="s((zone.x1 + zone.x2) / 2 + (zone.labelDx ?? 0))"
            :dy="labelLineDy(zoneLabelLines(zone).length, li)"
          >
            {{ line }}
          </tspan>
        </text>

        <!-- Arrows -->
        <g v-for="(arrow, i) in computedArrows" :key="'arrow-' + i">
          <line
            :x1="arrow.sx"
            :y1="arrow.sy"
            :x2="arrow.ex"
            :y2="arrow.ey"
            class="map-arrow"
            marker-end="url(#arrowhead)"
          />
          <text
            v-if="arrow.text"
            :x="(arrow.sx + arrow.ex) / 2"
            :y="(arrow.sy + arrow.ey) / 2 - 6"
            class="arrow-label"
          >
            {{ arrow.text }}
          </text>
        </g>

        <!-- Labels -->
        <text
          v-for="(label, i) in map.labels ?? []"
          :key="'label-' + i"
          :x="s(label.x)"
          :y="s(label.y)"
          class="map-label"
          :style="label.fontSize ? `font-size: ${label.fontSize}px` : ''"
          :transform="
            label.rotation ? `rotate(${label.rotation} ${s(label.x)} ${s(label.y)})` : undefined
          "
        >
          {{ label.text }}
        </text>

        <!-- Icons -->
        <path
          v-for="(icon, i) in map.icons ?? []"
          :key="'icon-' + i"
          :d="mapIconPath(icon.icon)"
          :transform="
            mapIconTransform(icon.icon, s(icon.x), s(icon.y), icon.size ?? DEFAULT_ICON_SIZE)
          "
          class="map-icon-svg"
        />
      </svg>
    </div>

    <!-- Map Key -->
    <div class="map-key">
      <div v-for="(item, i) in keyItems" :key="i" class="key-item">
        <span v-if="item.icon" class="key-icon" :class="item.colorClass">
          <FontAwesomeIcon :icon="item.icon" />
        </span>
        <span v-else class="key-circle" :class="item.colorClass"></span>
        <span class="key-label">{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// DUPLICATED from packages/frontend for the studio (intentional — see
// docs/superpowers/specs/2026-06-01-battleplan-authoring-tool-design.md §5).
// Keep rendering in sync with the frontend original when it changes.
import { computed } from 'vue';
import {
  type IBattleplanMap,
  type IMapArrow,
  type IMapZone,
  ObjectiveType,
  TerrainType,
  TerrainSize,
  ZoneType,
} from '@scrolldex/shared';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faTree,
  faWandSparkles,
  faHouseChimneyCrack,
  faStar,
  faCircle,
  faBiohazard,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

const props = defineProps<{
  map: IBattleplanMap;
}>();

// SVG dimensions - 60:44 ratio scaled up for crisp rendering
const SCALE = 8;
const W = 60 * SCALE;
const H = 44 * SCALE;

// Objectives: 40mm base + 6" control range → 6" + 40mm diameter
const OBJ_RADIUS = (6 + 40 / 25.4) / 2; // ≈ 3.787" radius

function s(inches: number): number {
  return inches * SCALE;
}

// Minor grid lines: every 15" width, every 11" height (excluding edges and center)
const minorGridX = [15, 45]; // 30 is the major line
const minorGridY = [11, 33]; // 22 is the major line

// Terrain radii - medium 7" diameter, large 1.5x objective
function terrainRadius(size: TerrainSize): number {
  switch (size) {
    case TerrainSize.Small:
      return s(2.5);
    case TerrainSize.Medium:
      return s(3.5); // 7" diameter
    case TerrainSize.Large:
      return s(OBJ_RADIUS * 1.5);
  }
}

function terrainIconDef(type: TerrainType): IconDefinition {
  switch (type) {
    case TerrainType.Obscuring:
      return faTree;
    case TerrainType.PlaceOfPower:
      return faWandSparkles;
    case TerrainType.Ruin:
    default:
      return faHouseChimneyCrack;
  }
}

function terrainIconTransform(type: TerrainType, cx: number, cy: number, size: number): string {
  const def = terrainIconDef(type);
  const [w, h] = def.icon as [number, number, string[], string, string | string[]];
  const scale = size / Math.max(w, h);
  const tx = cx - (w * scale) / 2;
  const ty = cy - (h * scale) / 2;
  return `translate(${tx},${ty}) scale(${scale})`;
}

function terrainIconPath(type: TerrainType): string {
  const def = terrainIconDef(type);
  const pathData = def.icon[4];
  return Array.isArray(pathData) ? (pathData as string[]).join(' ') : (pathData as string);
}

// Objectives that render a Font Awesome glyph instead of a letter (null → letter).
function objectiveIconDef(type: ObjectiveType): IconDefinition | null {
  switch (type) {
    case ObjectiveType.Regular:
      return faStar;
    default:
      return null;
  }
}

function objectiveIconTransform(type: ObjectiveType, cx: number, cy: number, size: number): string {
  const def = objectiveIconDef(type);
  if (!def) return '';
  const [w, h] = def.icon as [number, number, string[], string, string | string[]];
  const scale = size / Math.max(w, h);
  const tx = cx - (w * scale) / 2;
  const ty = cy - (h * scale) / 2;
  return `translate(${tx},${ty}) scale(${scale})`;
}

function objectiveIconPath(type: ObjectiveType): string {
  const def = objectiveIconDef(type);
  if (!def) return '';
  const pathData = def.icon[4];
  return Array.isArray(pathData) ? (pathData as string[]).join(' ') : (pathData as string);
}

// Freeform map icons: a small registry mirroring the studio editor. Unknown names
// fall back to a solid circle so saved bundles render even for icons added later.
const DEFAULT_ICON_SIZE = 28;
const MAP_ICON_DEFS: Record<string, IconDefinition> = {
  'house-chimney-crack': faHouseChimneyCrack,
  tree: faTree,
  'wand-sparkles': faWandSparkles,
  circle: faCircle,
  biohazard: faBiohazard,
};
function mapIconDef(name: string): IconDefinition {
  return MAP_ICON_DEFS[name] ?? faCircle;
}
function mapIconTransform(name: string, cx: number, cy: number, size: number): string {
  const def = mapIconDef(name);
  const [w, h] = def.icon as [number, number, string[], string, string | string[]];
  const scale = size / Math.max(w, h);
  const tx = cx - (w * scale) / 2;
  const ty = cy - (h * scale) / 2;
  return `translate(${tx},${ty}) scale(${scale})`;
}
function mapIconPath(name: string): string {
  const pathData = mapIconDef(name).icon[4];
  return Array.isArray(pathData) ? (pathData as string[]).join(' ') : (pathData as string);
}

function objectiveLabel(type: ObjectiveType): string {
  switch (type) {
    case ObjectiveType.Regular:
      return 'O';
    case ObjectiveType.Primary:
      return 'P';
    case ObjectiveType.Secondary:
      return 'S';
    case ObjectiveType.Oakenbrow:
      return 'O';
    case ObjectiveType.Gnarlroot:
      return 'G';
    case ObjectiveType.Heartwood:
      return 'H';
    case ObjectiveType.Winterleaf:
      return 'W';
    case ObjectiveType.GoldenLions:
      return 'GL';
    case ObjectiveType.SunSeekers:
      return 'SS';
    case ObjectiveType.Heldenhain:
      return 'H';
    case ObjectiveType.Sigmarite:
      return 'S';
  }
}

function zoneLabel(zone: IMapZone): string {
  const custom = zone.label?.trim();
  if (custom) return custom;
  switch (zone.type) {
    case ZoneType.Attacker:
      return 'Attacker';
    case ZoneType.Defender:
      return 'Defender';
    case ZoneType.Other:
      return '';
  }
}

// Split a zone label into rendered lines (custom labels may contain newlines).
function zoneLabelLines(zone: IMapZone): string[] {
  return zoneLabel(zone).split('\n');
}

// Vertical offset (in em) for each label line so the block stays centered.
function labelLineDy(count: number, index: number): string {
  return index === 0 ? `${(-(count - 1) * 0.55).toFixed(2)}em` : '1.1em';
}

// Compute arrow endpoints shortened to avoid overlapping objective circles
const ARROW_INSET = OBJ_RADIUS + 1; // pull back from objective center
const computedArrows = computed(() => {
  return (props.map.arrows ?? []).map((a: IMapArrow) => {
    const dx = s(a.x2 - a.x1);
    const dy = s(a.y2 - a.y1);
    const len = Math.sqrt(dx * dx + dy * dy);
    const inset = s(ARROW_INSET);
    const ux = dx / len;
    const uy = dy / len;
    return {
      sx: s(a.x1) + ux * inset,
      sy: s(a.y1) + uy * inset,
      ex: s(a.x2) - ux * inset,
      ey: s(a.y2) - uy * inset,
      text: a.text,
    };
  });
});

// Build key items from the unique objectives and terrain in this map
const objectiveNames: Record<ObjectiveType, string> = {
  [ObjectiveType.Regular]: 'Objective',
  [ObjectiveType.Primary]: 'Primary',
  [ObjectiveType.Secondary]: 'Secondary',
  [ObjectiveType.Oakenbrow]: 'Oakenbrow',
  [ObjectiveType.Gnarlroot]: 'Gnarlroot',
  [ObjectiveType.Heartwood]: 'Heartwood',
  [ObjectiveType.Winterleaf]: 'Winterleaf',
  [ObjectiveType.GoldenLions]: 'Golden Lions',
  [ObjectiveType.SunSeekers]: 'Sun Seekers',
  [ObjectiveType.Heldenhain]: 'Heldenhain',
  [ObjectiveType.Sigmarite]: 'Sigmarite',
};

const objectiveColorClass: Record<ObjectiveType, string> = {
  [ObjectiveType.Regular]: 'color-regular',
  [ObjectiveType.Primary]: 'color-primary-obj',
  [ObjectiveType.Secondary]: 'color-secondary-obj',
  [ObjectiveType.Oakenbrow]: 'color-oakenbrow',
  [ObjectiveType.Gnarlroot]: 'color-gnarlroot',
  [ObjectiveType.Heartwood]: 'color-heartwood',
  [ObjectiveType.Winterleaf]: 'color-winterleaf',
  [ObjectiveType.GoldenLions]: 'color-golden-lions',
  [ObjectiveType.SunSeekers]: 'color-sun-seekers',
  [ObjectiveType.Heldenhain]: 'color-heldenhain',
  [ObjectiveType.Sigmarite]: 'color-sigmarite',
};

// Objectives that show a Font Awesome glyph in the key instead of a colour dot
// (mirrors the board's objectiveIconDef).
const objectiveIcons: Partial<Record<ObjectiveType, string>> = {
  [ObjectiveType.Regular]: 'fa-star',
};

const terrainNames: Record<TerrainType, string> = {
  [TerrainType.Ruin]: 'Ruin',
  [TerrainType.Obscuring]: 'Obscuring',
  [TerrainType.PlaceOfPower]: 'Place of Power',
};

const terrainIcons: Record<TerrainType, string> = {
  [TerrainType.Ruin]: 'fa-house-chimney-crack',
  [TerrainType.Obscuring]: 'fa-tree',
  [TerrainType.PlaceOfPower]: 'fa-wand-sparkles',
};

const keyItems = computed(() => {
  const items: { label: string; icon?: string; colorClass: string }[] = [];

  // Unique objective types in this map
  const seenObjectives = new Set<ObjectiveType>();
  for (const obj of props.map.objectives) {
    if (!seenObjectives.has(obj.type)) {
      seenObjectives.add(obj.type);
      items.push({
        label: objectiveNames[obj.type],
        icon: objectiveIcons[obj.type],
        colorClass: objectiveColorClass[obj.type],
      });
    }
  }

  // Unique terrain types in this map
  const seenTerrain = new Set<TerrainType>();
  for (const t of props.map.terrain) {
    if (!seenTerrain.has(t.type)) {
      seenTerrain.add(t.type);
      items.push({
        label: terrainNames[t.type],
        icon: terrainIcons[t.type],
        colorClass: 'color-terrain',
      });
    }
  }

  // Icons with a legend, deduped per unique icon+legend pair.
  const seenIcons = new Set<string>();
  for (const ic of props.map.icons ?? []) {
    const legend = ic.legend?.trim();
    if (!legend) continue;
    const key = `${ic.icon}|${legend}`;
    if (seenIcons.has(key)) continue;
    seenIcons.add(key);
    items.push({ label: legend, icon: `fa-${ic.icon}`, colorClass: 'color-icon' });
  }

  return items;
});
</script>

<style scoped>
.battleplan-map {
  padding: 0.5em 0;
}

.map-container {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.battleplan-svg {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 0.25em;
}

.map-bg {
  fill: var(--bg-secondary);
}

.map-border {
  fill: none;
  stroke: var(--text-accent);
  stroke-width: 2;
}

.grid-line-minor {
  stroke: var(--text-accent);
  stroke-width: 0.5;
  opacity: 0.15;
}

.grid-line-major {
  stroke: var(--text-accent);
  stroke-width: 1;
  opacity: 0.3;
}

/* Zones */
.zone-attacker {
  fill: rgba(200, 60, 60, 0.15);
  stroke: rgba(200, 60, 60, 0.4);
  stroke-width: 1.5;
  stroke-dasharray: 8 4;
}

.zone-defender {
  fill: rgba(60, 120, 200, 0.15);
  stroke: rgba(60, 120, 200, 0.4);
  stroke-width: 1.5;
  stroke-dasharray: 8 4;
}

.zone-other {
  fill: rgba(180, 180, 60, 0.12);
  stroke: rgba(180, 180, 60, 0.35);
  stroke-width: 1.5;
  stroke-dasharray: 8 4;
}

.zone-label {
  fill: var(--text-accent);
  font-size: 16px;
  font-weight: 600;
  text-anchor: middle;
  dominant-baseline: central;
  opacity: 0.5;
  text-transform: uppercase;
  letter-spacing: 2px;
}

/* Objectives */
.objective {
  stroke-width: 2;
}

.objective-regular {
  fill: var(--color-yellow);
  stroke: var(--color-yellow);
  opacity: 0.7;
}

.objective-primary {
  fill: rgba(255, 215, 0, 0.9);
  stroke: #b8960f;
}

.objective-secondary {
  fill: rgba(192, 192, 192, 0.9);
  stroke: #888;
}

.objective-oakenbrow {
  fill: var(--color-red);
  stroke: var(--color-red);
  opacity: 0.7;
}

.objective-gnarlroot {
  fill: var(--color-green);
  stroke: var(--color-green);
  opacity: 0.7;
}

.objective-heartwood {
  fill: var(--color-blue);
  stroke: var(--color-blue);
  opacity: 0.7;
}

.objective-winterleaf {
  fill: var(--color-purple);
  stroke: var(--color-purple);
  opacity: 0.7;
}

.objective-golden_lions {
  fill: var(--color-red);
  stroke: var(--color-red);
  opacity: 0.7;
}

.objective-sun_seekers {
  fill: var(--color-yellow);
  stroke: var(--color-yellow);
  opacity: 0.7;
}

.objective-heldenhain {
  fill: var(--color-black);
  stroke: var(--color-black);
  opacity: 0.7;
}

.objective-sigmarite {
  fill: var(--color-blue);
  stroke: var(--color-blue);
  opacity: 0.7;
}

.objective-label {
  fill: #fff;
  font-size: 13px;
  font-weight: 700;
  text-anchor: middle;
  dominant-baseline: central;
}

/* Objective glyph (e.g. the regular-objective star) drawn in place of a letter. */
.objective-icon-svg {
  fill: #fff;
}

/* Terrain */
.terrain {
  stroke-width: 1.5;
}

.terrain-ruin,
.terrain-obscuring,
.terrain-place_of_power {
  fill: var(--color-gray);
  stroke: var(--color-gray);
  opacity: 0.5;
}

.terrain-icon-svg {
  fill: var(--text-main);
  font-size: 14px;
  opacity: 0.8;
}

.map-icon-svg {
  fill: var(--text-main);
  opacity: 0.85;
}

/* Map Key */
.map-key {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75em;
  padding: 0.5em 0;
  max-width: 600px;
  margin: 0 auto;
}

.key-item {
  display: flex;
  align-items: center;
  gap: 0.35em;
  font-size: 0.8em;
  color: var(--text-accent);
}

.key-circle {
  width: 0.9em;
  height: 0.9em;
  border-radius: 50%;
  flex-shrink: 0;
}

.key-icon {
  font-size: 0.9em;
  flex-shrink: 0;
}

/* Key color classes */
.color-regular {
  color: var(--color-yellow);
}

.color-primary-obj {
  background-color: rgba(255, 215, 0, 0.9);
  border: 1px solid #b8960f;
}

.color-secondary-obj {
  background-color: rgba(192, 192, 192, 0.9);
  border: 1px solid #888;
}

.color-oakenbrow {
  background-color: var(--color-red);
  color: var(--color-red);
}

.color-gnarlroot {
  background-color: var(--color-green);
  color: var(--color-green);
}

.color-heartwood {
  background-color: var(--color-blue);
  color: var(--color-blue);
}

.color-winterleaf {
  background-color: var(--color-purple);
  color: var(--color-purple);
}

.color-golden-lions {
  background-color: var(--color-red);
  color: var(--color-red);
}

.color-sun-seekers {
  background-color: var(--color-yellow);
  color: var(--color-yellow);
}

.color-heldenhain {
  background-color: var(--color-black);
  color: var(--color-black);
}

.color-sigmarite {
  background-color: var(--color-blue);
  color: var(--color-blue);
}

.color-terrain {
  color: var(--color-gray);
}

.color-icon {
  color: var(--text-main);
}

.key-label {
  white-space: nowrap;
}

/* Arrows */
.arrowhead {
  fill: var(--text-accent);
  opacity: 0.7;
}

.map-arrow {
  stroke: var(--text-accent);
  stroke-width: 2;
  opacity: 0.7;
}

.arrow-label {
  fill: var(--text-accent);
  font-size: 11px;
  font-weight: 600;
  text-anchor: middle;
  dominant-baseline: auto;
  opacity: 0.7;
}

/* Labels */
.map-label {
  fill: var(--text-accent);
  font-size: 12px;
  font-weight: 600;
  text-anchor: middle;
  dominant-baseline: central;
  opacity: 0.7;
}
</style>
